
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

interface TimeEntryData {
  employé: string;
  chantier: string;
  date: string;
  entrée: string;
  sortie: string;
  total: string;
  commentaire: string;
  jour_semaine: string;
  semaine: number;
  annee: number;
}

interface WeeklyTimesheet {
  employe: string;
  semaine: number;
  annee: number;
  chantiers: {
    nom: string;
    lundi?: { heures: string; autres: string };
    mardi?: { heures: string; autres: string };
    mercredi?: { heures: string; autres: string };
    jeudi?: { heures: string; autres: string };
    vendredi?: { heures: string; autres: string };
    total: string;
  }[];
  commentaires: string[];
}

/**
 * Exporte les données au format AVEM (feuille de pointage hebdomadaire)
 */
export const exportToAvemFormat = async (
  data: TimeEntryData[],
  fileName: string = "feuille-pointage-avem"
): Promise<boolean> => {
  try {
    if (!data || data.length === 0) {
      console.warn("Aucune donnée à exporter");
      return false;
    }

    // Grouper les données par employé et par semaine
    const weeklyTimesheets = groupByEmployeeAndWeek(data);
    
    // Créer un nouveau classeur Excel
    const workbook = XLSX.utils.book_new();
    
    // Créer une feuille pour chaque employé/semaine
    weeklyTimesheets.forEach((timesheet, index) => {
      const worksheetData = createAvemWorksheetData(timesheet);
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Définir la largeur des colonnes
      worksheet['!cols'] = [
        { wch: 20 }, // Nom du chantier
        { wch: 12 }, // N° Affaire
        { wch: 10 }, // Lundi heures
        { wch: 10 }, // Lundi autres
        { wch: 10 }, // Mardi heures
        { wch: 10 }, // Mardi autres
        { wch: 10 }, // Mercredi heures
        { wch: 10 }, // Mercredi autres
        { wch: 10 }, // Jeudi heures
        { wch: 10 }, // Jeudi autres
        { wch: 10 }, // Vendredi heures
        { wch: 10 }, // Vendredi autres
        { wch: 12 }, // Total
        { wch: 20 }  // Commentaires
      ];
      
      // Nom de la feuille
      const sheetName = `${timesheet.employe.substring(0, 15)}_S${timesheet.semaine}`;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
    
    // Sauvegarder le fichier
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${fileName}.xlsx`);
    
    return true;
  } catch (error) {
    console.error("Erreur lors de l'export AVEM:", error);
    return false;
  }
};

/**
 * Groupe les données par employé et par semaine
 */
function groupByEmployeeAndWeek(data: TimeEntryData[]): WeeklyTimesheet[] {
  const groupedData = new Map<string, WeeklyTimesheet>();
  
  data.forEach(entry => {
    const key = `${entry.employé}_${entry.semaine}_${entry.annee}`;
    
    if (!groupedData.has(key)) {
      groupedData.set(key, {
        employe: entry.employé,
        semaine: entry.semaine,
        annee: entry.annee,
        chantiers: [],
        commentaires: []
      });
    }
    
    const timesheet = groupedData.get(key)!;
    
    // Trouver ou créer l'entrée pour ce chantier
    let chantierEntry = timesheet.chantiers.find(c => c.nom === entry.chantier);
    if (!chantierEntry) {
      chantierEntry = {
        nom: entry.chantier,
        total: "0h"
      };
      timesheet.chantiers.push(chantierEntry);
    }
    
    // Ajouter les heures selon le jour de la semaine
    const dayKey = getDayKey(entry.jour_semaine);
    if (dayKey) {
      chantierEntry[dayKey] = {
        heures: entry.total,
        autres: "" // Pour l'instant, pas de données "autres"
      };
    }
    
    // Ajouter les commentaires
    if (entry.commentaire && !timesheet.commentaires.includes(entry.commentaire)) {
      timesheet.commentaires.push(entry.commentaire);
    }
  });
  
  // Calculer les totaux pour chaque chantier
  groupedData.forEach(timesheet => {
    timesheet.chantiers.forEach(chantier => {
      let totalHeures = 0;
      ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].forEach(day => {
        const dayData = chantier[day as keyof typeof chantier];
        if (dayData && typeof dayData === 'object' && 'heures' in dayData) {
          const heures = parseFloat(dayData.heures.replace('h', '')) || 0;
          totalHeures += heures;
        }
      });
      chantier.total = `${totalHeures}h`;
    });
  });
  
  return Array.from(groupedData.values());
}

/**
 * Convertit le nom du jour en clé utilisable
 */
function getDayKey(jourSemaine: string): string | null {
  const dayMap: Record<string, string> = {
    'lundi': 'lundi',
    'mardi': 'mardi',
    'mercredi': 'mercredi',
    'jeudi': 'jeudi',
    'vendredi': 'vendredi'
  };
  
  return dayMap[jourSemaine.toLowerCase()] || null;
}

/**
 * Crée les données de la feuille de calcul au format AVEM
 */
function createAvemWorksheetData(timesheet: WeeklyTimesheet): any[][] {
  const data: any[][] = [];
  
  // En-tête AVEM
  data.push(['AVEM', '', '', '', 'FEUILLE DE POINTAGES', '', '', '', '', '', '', '', '', '']);
  data.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  
  // Informations employé
  data.push(['NOM:', timesheet.employe, '', '', 'Semaine N°:', timesheet.semaine, '', '', '', '', '', '', '', '']);
  data.push(['Prénom:', '', '', '', 'Du:', 'Lundi', '', 'Au:', 'Dimanche', '', '', '', '', '']);
  data.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  
  // En-tête du tableau
  data.push([
    'Nom du chantier',
    'N° Affaire',
    'Lundi',
    '',
    'Mardi', 
    '',
    'Mercredi',
    '',
    'Jeudi',
    '',
    'Vendredi',
    '',
    'Total',
    'Commentaires'
  ]);
  
  data.push([
    '',
    '',
    'Heures travaillées',
    'Autres',
    'Heures travaillées',
    'Autres',
    'Heures travaillées',
    'Autres',
    'Heures travaillées',
    'Autres',
    'Heures travaillées',
    'Autres',
    '',
    ''
  ]);
  
  // Données des chantiers
  timesheet.chantiers.forEach(chantier => {
    data.push([
      chantier.nom,
      '', // N° Affaire - à implémenter si nécessaire
      chantier.lundi?.heures || '',
      chantier.lundi?.autres || '',
      chantier.mardi?.heures || '',
      chantier.mardi?.autres || '',
      chantier.mercredi?.heures || '',
      chantier.mercredi?.autres || '',
      chantier.jeudi?.heures || '',
      chantier.jeudi?.autres || '',
      chantier.vendredi?.heures || '',
      chantier.vendredi?.autres || '',
      chantier.total,
      '' // Commentaires spécifiques au chantier
    ]);
  });
  
  // Lignes vides pour les chantiers non utilisés
  for (let i = timesheet.chantiers.length; i < 10; i++) {
    data.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  }
  
  // Section commentaires
  data.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  data.push(['Notes et commentaires:', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  
  // Ajouter les commentaires collectés
  timesheet.commentaires.forEach((commentaire, index) => {
    if (index < 4) { // Limiter à 4 lignes de commentaires
      data.push([commentaire, '', '', '', '', '', '', '', '', '', '', '', '', '']);
    }
  });
  
  // Compléter avec des lignes vides si nécessaire
  for (let i = timesheet.commentaires.length; i < 4; i++) {
    data.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  }
  
  return data;
}
